import { useState } from 'react';
import * as Dialog from '@radix-ui/react-dialog';
import { Button } from '@ui';
import { X } from '@ui/icons';
import { cn, formatDecimalWithCommas } from '@shared/lib';
import type { BreakdownItem } from './BreakdownModal';
import type { SplitMember } from './SplitModal';
import { MemberPickerModal } from './MemberPickerModal';
import {
  computeAssignedShares,
  signedChargeAmount,
  type ItemAssignment,
} from '../model/assignItems';

export type { ItemAssignment } from '../model/assignItems';

export interface AssignItemsModalProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  items: BreakdownItem[];
  feesAndTaxes: BreakdownItem[];
  currencySymbol: string;
  members: SplitMember[];
  /** Emits the per-item assignments AND the additional charges (entered in the prior Itemization
   *  step — unchanged here) so the caller can persist both in one place. */
  onSave: (assignments: ItemAssignment[], feesAndTaxes: BreakdownItem[]) => void;
}

/** Format a price string to a number (fallback 0). */
const toNum = (v: string) => parseFloat(v) || 0;

/**
 * "Assign each item" modal — shows a table of items with per-item split type and
 * member assignment, plus a right-hand grand-total panel per member.
 */
export function AssignItemsModal({
  open,
  onOpenChange,
  items,
  feesAndTaxes,
  currencySymbol,
  members,
  onSave,
}: AssignItemsModalProps) {
  // Picker state
  const [pickerOpen, setPickerOpen] = useState(false);
  const [pickerItemId, setPickerItemId] = useState<string | null>(null);

  // Per-item assignments
  const [assignments, setAssignments] = useState<ItemAssignment[]>(() =>
    items.map((item) => ({
      itemId: item.id,
      splitType: 'full',
      assignedMemberIds: [],
    })),
  );

  const getAssignment = (itemId: string): ItemAssignment =>
    assignments.find((a) => a.itemId === itemId) ?? {
      itemId,
      splitType: 'full',
      assignedMemberIds: [],
    };

  const updateAssignment = (itemId: string, patch: Partial<ItemAssignment>) => {
    setAssignments((prev) => prev.map((a) => (a.itemId === itemId ? { ...a, ...patch } : a)));
  };

  const openPicker = (itemId: string) => {
    setPickerItemId(itemId);
    setPickerOpen(true);
  };

  const handlePickerAssign = (memberIds: string[]) => {
    if (pickerItemId) {
      updateAssignment(pickerItemId, { assignedMemberIds: memberIds });
    }
  };

  // ── Grand total computation ──────────────────────────────────────────────
  const totalExtras = feesAndTaxes.reduce(
    (s, f) => s + signedChargeAmount(f.name, f.price, f.sign),
    0,
  );

  const grandTotal = items.reduce((s, i) => s + toNum(i.price), 0) + totalExtras;

  // Per-member items + extras — the SAME helper the bill's split is derived from on Save, so the
  // panel's figures are exactly what ends up in the expense's split.
  const memberShares = computeAssignedShares(items, feesAndTaxes, assignments);

  // Save is enabled only once EVERY item has at least one member assigned — an unassigned item
  // leaves its amount unallocated, so the split can't be saved yet.
  const allItemsAssigned =
    items.length > 0 && items.every((item) => getAssignment(item.id).assignedMemberIds.length > 0);

  // ── Picker data ──────────────────────────────────────────────────────────
  const pickerItem = pickerItemId ? items.find((i) => i.id === pickerItemId) : null;
  const pickerAssignment = pickerItemId ? getAssignment(pickerItemId) : null;

  return (
    <>
      <Dialog.Root open={open} onOpenChange={onOpenChange}>
        <Dialog.Portal>
          <Dialog.Overlay className="fixed inset-0 z-70 bg-black/50" />
          <Dialog.Content
            aria-describedby={undefined}
            onPointerDownOutside={(e) => e.preventDefault()}
            onInteractOutside={(e) => e.preventDefault()}
            className="fixed left-1/2 top-1/2 z-70 flex max-h-[92vh] w-[calc(100vw-2rem)] max-w-[1100px] -translate-x-1/2 -translate-y-1/2 flex-col overflow-hidden rounded-2xl bg-surface-raised shadow-overlay focus:outline-none"
          >
            {/* Header */}
            <div className="flex items-center justify-between gap-4 border-b border-border bg-header-bar px-5 py-3">
              <Dialog.Title className="text-base font-semibold text-fg-strong">
                Assign each item
              </Dialog.Title>
              <Dialog.Close asChild>
                <button
                  id="assign-items-close-btn"
                  type="button"
                  aria-label="Close"
                  className="flex size-7 shrink-0 cursor-pointer items-center justify-center rounded-full bg-border-soft hover:bg-border focus-visible:outline-none"
                >
                  <X className="size-4" />
                </button>
              </Dialog.Close>
            </div>

            {/* Body — two-panel layout */}
            <div className="flex min-h-0 flex-1 overflow-hidden">
              {/* ── Left panel ────────────────────────────────────────── */}
              <div className="flex min-h-0 flex-1 flex-col overflow-y-auto">
                {/* Tip */}
                <p className="px-5 py-3 text-xs text-muted">
                  <strong className="font-semibold text-fg">Tip:</strong> Tap an item, then select
                  who should pay for it. If you pick more than one people, it splits equally.
                </p>

                {/* Items table */}
                <div className="mx-5 border border-border-soft rounded-md overflow-hidden">
                  <table className="w-full text-sm border-collapse divide-y divide-border-soft">
                    <thead>
                      <tr className="bg-canvas text-xs font-semibold text-muted">
                        <th className="px-4 py-3 text-left font-semibold border-r border-border-soft">
                          Expense
                        </th>
                        <th className="px-4 py-3 text-left font-semibold border-r border-border-soft">
                          Amount
                        </th>
                        <th className="px-4 py-3 text-left font-semibold border-r border-border-soft min-w-[150px]">
                          Split Type
                        </th>
                        <th className="px-4 py-3 text-left font-semibold border-r border-border-soft min-w-[140px]">
                          Split with
                        </th>
                        <th className="px-4 py-3 text-left font-semibold pr-4">Assigned To</th>
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-border-soft">
                      {items.map((item) => {
                        const a = getAssignment(item.id);
                        const price = toNum(item.price);
                        const n = a.assignedMemberIds.length;
                        const share = a.splitType === 'each' ? price : n > 0 ? price / n : price;
                        const assignedNames = a.assignedMemberIds
                          .map((id) => members.find((m) => m.id === id)?.name ?? '')
                          .filter(Boolean);

                        return (
                          <tr key={item.id} className="hover:bg-canvas/20 transition-colors">
                            {/* Expense name */}
                            <td className="px-4 py-3.5 font-medium text-fg border-r border-border-soft">
                              {item.name || <span className="text-muted italic">Unnamed</span>}
                            </td>

                            {/* Amount */}
                            <td className="px-4 py-3.5 text-left font-semibold text-fg border-r border-border-soft">
                              {currencySymbol}
                              {formatDecimalWithCommas(price.toFixed(2))}
                            </td>

                            {/* Split Type — clickable */}
                            <td className="px-4 py-3.5 border-r border-border-soft">
                              <button
                                type="button"
                                onClick={() => {
                                  // Toggle split type
                                  updateAssignment(item.id, {
                                    splitType: a.splitType === 'each' ? 'full' : 'each',
                                  });
                                }}
                                className="cursor-pointer text-left text-sm text-[#354C8F] hover:underline"
                              >
                                <span className="font-medium">
                                  {a.splitType === 'each' ? 'Each' : 'Full amount'}{' '}
                                </span>
                                <span className="font-bold">
                                  {currencySymbol}
                                  {formatDecimalWithCommas(share.toFixed(2))}
                                </span>
                              </button>
                            </td>

                            {/* Split with */}
                            <td className="px-4 py-3.5 border-r border-border-soft text-sm font-medium text-fg">
                              {assignedNames.length === 0 ? (
                                <span className="text-danger">Unassigned</span>
                              ) : assignedNames.length === 1 ? (
                                <span>{assignedNames[0]}</span>
                              ) : assignedNames.length === 2 ? (
                                <span>{assignedNames.join(', ')}</span>
                              ) : (
                                <span>
                                  {assignedNames[0]} +{assignedNames.length - 1}
                                </span>
                              )}
                            </td>

                            {/* Assigned To badge — clickable */}
                            <td className="px-4 py-3.5">
                              <button
                                type="button"
                                id={`assign-item-badge-${item.id}`}
                                onClick={() => openPicker(item.id)}
                                className={cn(
                                  'cursor-pointer rounded-full px-4 py-1.5 text-xs font-semibold transition-colors border',
                                  n > 0
                                    ? 'bg-[#EAF6EC] text-[#2A7B39] border-[#CBEAD0] hover:bg-[#DDF1E2]'
                                    : 'bg-[#FFF4EC] text-[#D06000] border-[#FFE2CC] hover:bg-[#FFEBDC]',
                                )}
                              >
                                {n > 0 ? `${n} Member` : 'Assign member'}
                              </button>
                            </td>
                          </tr>
                        );
                      })}
                    </tbody>
                  </table>
                </div>
              </div>

              {/* ── Right panel (Grand Total) ──────────────────────────── */}
              <div className="hidden w-70 shrink-0 flex-col gap-4 px-2 pr-5 py-4 md:flex overflow-y-auto">
                <div className="flex items-center justify-between">
                  <span className="text-sm font-semibold text-fg-strong">Grand Total</span>
                  <span className="text-sm font-bold text-fg-strong tabular-nums">
                    {currencySymbol}
                    {formatDecimalWithCommas(grandTotal.toFixed(2))}
                  </span>
                </div>

                <div className="flex flex-col gap-3">
                  {members.map((member) => {
                    const itemAmt = memberShares[member.id]?.items ?? 0;
                    const extraAmt = memberShares[member.id]?.extras ?? 0;
                    const total = itemAmt + extraAmt;
                    const isUnassigned = itemAmt === 0 && extraAmt === 0;

                    return (
                      <div
                        key={member.id}
                        className={cn(
                          'flex items-center gap-3 rounded-sm border border-border-soft bg-surface-raised p-3',
                          isUnassigned ? 'opacity-50' : 'shadow-xs',
                        )}
                      >
                        {/* Colour bar */}
                        <div className={cn('w-1.5 h-10 rounded-full shrink-0 bg-[#4C4ACA]')} />
                        <div className="min-w-0 flex-1">
                          <p className={cn('text-sm font-semibold truncate', 'text-[#191C1E]')}>
                            {member.name}
                          </p>
                          {isUnassigned ? (
                            <p className="text-xs text-[#414755] font-normal mt-0.5">Unassigned</p>
                          ) : (
                            <p className="text-xs text-[#414755] font-normal mt-0.5">
                              Items {currencySymbol}
                              {formatDecimalWithCommas(itemAmt.toFixed(2))} | Extras{' '}
                              {currencySymbol}
                              {formatDecimalWithCommas(extraAmt.toFixed(2))}
                            </p>
                          )}
                        </div>
                        <span
                          className={cn('shrink-0 text-sm font-bold tabular-nums text-[#414755]')}
                        >
                          {currencySymbol}
                          {formatDecimalWithCommas(total.toFixed(2))}
                        </span>
                      </div>
                    );
                  })}
                </div>
              </div>
            </div>

            {/* Footer */}
            <div className="flex items-center gap-2.5 border-t border-border-soft bg-surface-raised px-5 py-3">
              <Button
                id="assign-items-save-btn"
                variant="primary"
                size="md"
                disabled={!allItemsAssigned}
                onClick={() => {
                  // Additional charges are entered in the Itemization step, not here — pass them
                  // through unchanged so the caller persists the itemization (drives the "View
                  // breakdown" state).
                  onSave(assignments, feesAndTaxes);
                  onOpenChange(false);
                }}
              >
                Save
              </Button>
              <Button
                id="assign-items-cancel-btn"
                variant="secondary"
                size="md"
                onClick={() => onOpenChange(false)}
              >
                Cancel
              </Button>
            </div>
          </Dialog.Content>
        </Dialog.Portal>
      </Dialog.Root>

      {/* Member picker sub-modal */}
      {pickerItem && pickerAssignment && (
        <MemberPickerModal
          key={pickerItemId}
          open={pickerOpen}
          onOpenChange={setPickerOpen}
          itemName={pickerItem.name || 'Item'}
          itemPrice={toNum(pickerItem.price)}
          currencySymbol={currencySymbol}
          members={members}
          selectedIds={pickerAssignment.assignedMemberIds}
          onAssign={handlePickerAssign}
        />
        // <></>
      )}
    </>
  );
}
